import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FolderIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { dispatchWithToast } from "../../../Utils";
import { createProjectFolderAsync } from "../../../../slices/project/folder";
import {
  FOLDER_ACCESS_ADMINS,
  FOLDER_ACCESS_PROJECT,
  FolderAccessFields,
} from "./folder-access-fields";

export const FolderCreateDialog = ({
  open,
  onClose,
  project,
  onFolderCreated,
  parentId = null,
  isArchiveAdmin = false,
  parentRestricted = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleSubmit = (values, helpers) => {
    try {
      dispatchWithToast(
        dispatch(createProjectFolderAsync(values)),
        (newFolder) => {
          if (onFolderCreated) {
            onFolderCreated(newFolder);
          }
          onClose();
        },
        () => {
          helpers.setSubmitting(false);
        }
      );

      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
    } catch (err) {
      toast.error(t("document_archive.folder_create_error"));
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="md"
      title="document_archive.new_folder"
      closeOnOutsideClick
      panelClassName="rounded-[22px] border border-[#EDF0EF] shadow-[0_24px_60px_-20px_rgba(16,34,31,0.22)]"
    >
      <div className="px-1 pb-1 text-left">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[#E6F2F0] text-[#0F766E]">
            <FolderIcon className="h-5 w-5" />
          </div>
          <p className="text-[13px] text-[#8B9895]">
            {parentId
              ? t("document_archive.folder_create_subfolder")
              : t("document_archive.folder_create_root")}
          </p>
        </div>

        <Formik
          enableReinitialize
          initialValues={{
            id: null,
            project_id: project?.id,
            parent_id: parentId || null,
            title: "",
            description: "",
            access: parentRestricted ? FOLDER_ACCESS_ADMINS : FOLDER_ACCESS_PROJECT,
            submit: null,
          }}
          validationSchema={Yup.object({
            project_id: Yup.string().required(),
            title: Yup.string().required(t("document_archive.folder_title_required")),
          })}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium text-[#4A5A57]">
                  {t("document_archive.folder_title")}
                </span>
                <input
                  name="title"
                  type="text"
                  autoFocus
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("document_archive.folder_title_placeholder")}
                  className="h-11 rounded-[16px] border border-[#E5E9E8] bg-[#F5F7F6] px-4 text-sm text-[#10221F] outline-none placeholder:text-[#93A19E] focus:border-[#0F766E] focus:bg-white"
                />
                {formik.touched.title && formik.errors.title ? (
                  <span className="text-xs text-red-600">{formik.errors.title}</span>
                ) : null}
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium text-[#4A5A57]">
                  {t("document_archive.folder_description")}
                </span>
                <textarea
                  name="description"
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder={t("document_archive.folder_description_placeholder")}
                  className="resize-none rounded-[16px] border border-[#E5E9E8] bg-[#F5F7F6] px-4 py-3 text-sm text-[#10221F] outline-none placeholder:text-[#93A19E] focus:border-[#0F766E] focus:bg-white"
                />
              </label>

              {isArchiveAdmin ? (
                <FolderAccessFields
                  value={formik.values.access}
                  inherited={parentRestricted}
                  disabled={parentRestricted}
                  onChange={(access) => formik.setFieldValue("access", access)}
                />
              ) : null}

              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-10 flex-1 items-center justify-center rounded-[20px] border border-[#E5E9E8] text-sm font-medium text-[#4A5A57] hover:bg-[#F0F3F2]"
                >
                  {t("document_archive.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="flex h-10 flex-1 items-center justify-center rounded-[20px] bg-[#0F766E] text-sm font-semibold text-white hover:bg-[#0B5A54] disabled:bg-[#A6B2AF]"
                >
                  {formik.isSubmitting
                    ? t("document_archive.creating")
                    : t("document_archive.create_folder")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PMDialog>
  );
};
